import { configureStore, combineReducers } from "@reduxjs/toolkit";
import { persistStore, persistReducer, createMigrate } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { encryptTransform } from "redux-persist-transform-encrypt";
import authReducer from "./Reducer/authReducer";
import addCampaignReducer from "./Reducer/addCampaignReducer";
import commonReducer from "./Reducer/commonReducer";
import addWalmartCampaignReducer from "./Reducer/addWalmartCampaignReducer";
import createSdCampaignReducer from "./Reducer/createSdCampaignReducer";
import createSbCampaignReducer from "./Reducer/createSbCampaignReducer";

//encryptor
const encryptor = encryptTransform({
  secretKey: "b7F2@Xv93!mZpQ1#dL8o$C6wK5YtNgR",
  onError: function (error) {
    console.error(error);
  },
});

// redux-persist's default reconciler (autoMergeLevel1) restores each top-level
// slice WHOLE — a persisted `addCampaign` is NOT merged with the reducer's
// initial state. So a wizard state saved before a new campaign type existed
// keeps its old shape forever: Session 3E's `step_3.type_5` / `step_5.target_5`
// sub-states would simply be absent, and the wizard would write into
// `undefined` — at best a crash, at worst an SB campaign with no
// `campaign_type` being created as a Sponsored Products one on a live account.
//
// The wizard is transient work-in-progress and already has a reset action, so
// dropping that one slice is the safe upgrade. Everything else (auth, the
// selected profile) is left untouched — purging the whole store here would log
// every user out. createMigrate runs this once, at version -1 -> 1.
//
// Session F11 re-runs the same drop at version 2, for the same reason pointing
// the other way: Sponsored Display and Sponsored Brands have now been REMOVED
// from the 8-step wizard, so a slice persisted mid-flow still carries
// `step_3.type_4` / `type_5` ticked and `step_5.target_3/4/5` filled. Restored
// whole, that state would open Step 5 for a campaign type Step 3 no longer
// offers and hand Step 7 rows whose builders have been deleted. Dropping the
// slice once puts every user back on the reducer's own SP-only initial state.
const migrations = {
  1: (state) => {
    const { addCampaign, ...rest } = state || {};
    return rest;
  },
  2: (state) => {
    const { addCampaign, ...rest } = state || {};
    return rest;
  },
};

// Session A2: the two v2 create wizards are NOT persisted. The hazard the
// migration above exists to clean up — autoMergeLevel1 restoring a slice WHOLE,
// so a shape written before a field existed survives forever — applies just as
// much to these, and their shape is going to keep moving all the way through
// Phases B, C and D. Blacklisting them means every mount starts from the
// reducer's own initialState, which is what a create wizard should do anyway;
// there is nothing here worth surviving a refresh, and it removes the need for
// a migration per shape change.
const persistConfig = {
  key: "root",
  version: 2,
  storage,
  transforms: [encryptor],
  migrate: createMigrate(migrations),
  blacklist: ["createSdCampaign", "createSbCampaign"],
};

const rootReducer = combineReducers({
  auth: authReducer,
  addCampaign: addCampaignReducer,
  commonReducer:commonReducer,
  addWalmartCampaign: addWalmartCampaignReducer,
  createSdCampaign: createSdCampaignReducer,
  createSbCampaign: createSbCampaignReducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ["persist/PERSIST"],
        ignoredPaths: ["register"],
      },
    }),
});

export const persistor = persistStore(store);

export default store;
